/**
 * PHASE 9 — Enterprise: generic approval engine, notification helpers,
 * finance document links, SCM reporting aggregates.
 * Additive — does not replace existing ApprovalService / NotificationService / Dashboard.
 */

import {
  collection,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  serverTimestamp
} from "./fs";
import { db } from "../config/firebase";
import { AuditService, NotificationService, InvoiceService } from "./firestore";
import { StatusHistoryService } from "./statusHistoryService";

import { listOwned } from "./ownership";

function codeStamp(prefix) {
  const ts = Date.now().toString(36).toUpperCase().slice(-5);
  const rand = Math.random().toString(36).toUpperCase().slice(2, 6);
  return `${prefix}-${ts}-${rand}`;
}

async function listOrdered(collectionName, max = 200) {
  return listOwned(collectionName, { max });
}

async function countCollection(name) {
  try {
    return (await listOwned(name)).length;
  } catch {
    return 0;
  }
}

export const APPROVAL_ENTITY_TYPES = [
  "purchaseOrders",
  "salesOrders",
  "exportOrders",
  "manufacturingOrders",
  "salesReturns",
  "generic"
];

export const APPROVAL_TRANSITIONS = {
  pending: ["approved", "rejected"],
  approved: [],
  rejected: []
};

/** Generic multi-entity approval requests (alongside legacy approvalRequests). */
export const ApprovalEngineService = {
  transitions: APPROVAL_TRANSITIONS,

  async list(statusFilter = null) {
    const all = await listOrdered("approvalWorkflows");
    if (!statusFilter) return all;
    return all.filter((a) => a.status === statusFilter);
  },

  async listPending() {
    return this.list("pending");
  },

  async get(id) {
    const snap = await getDoc(doc(db, "approvalWorkflows", id));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  async submit({
    entityType,
    entityId,
    entityCode = "",
    title = "",
    amount = null,
    currency = "USD",
    requestedBy,
    requestedByName = "",
    notes = ""
  }) {
    if (!entityType || !entityId) throw new Error("ENTITY_REQUIRED");
    if (!APPROVAL_ENTITY_TYPES.includes(entityType) && entityType !== "generic") {
      // allow custom types too
    }
    const payload = {
      code: codeStamp("APR"),
      entityType,
      entityId,
      entityCode,
      title: title || `Approve ${entityCode || entityId}`,
      amount: amount != null ? Number(amount) : null,
      currency,
      status: "pending",
      requestedBy: requestedBy || null,
      requestedByName,
      decidedBy: null,
      decidedAt: null,
      decisionNote: "",
      notes,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      deletedAt: null
    };
    const ref = await addDoc(collection(db, "approvalWorkflows"), payload);
    await StatusHistoryService.append({
      entityType: "approvalWorkflows",
      entityId: ref.id,
      previousStatus: null,
      newStatus: "pending",
      userId: requestedBy
    });
    await NotificationEngineService.notifyUser(requestedBy, "approval", `Approval submitted: ${payload.code}`);
    await AuditService.logChange({
      userId: requestedBy,
      action: "APPROVAL_SUBMIT",
      entityType: "approvalWorkflows",
      entityId: ref.id,
      newValue: { code: payload.code, entityType, entityId }
    });
    return { id: ref.id, ...payload };
  },

  async decide(id, decision, userId, note = "") {
    if (!["approved", "rejected"].includes(decision)) throw new Error("INVALID_DECISION");
    const item = await this.get(id);
    if (!item) throw new Error("NOT_FOUND");
    if (item.status !== "pending") throw new Error("ALREADY_DECIDED");
    // Görevler ayrılığı (AC-05): talep eden kendi talebinde karar veremez.
    if (!userId) throw new Error("NOT_SIGNED_IN");
    if (item.requestedBy === userId || item.createdBy === userId) throw new Error("SELF_APPROVAL_FORBIDDEN");

    await updateDoc(doc(db, "approvalWorkflows", id), {
      status: decision,
      decidedBy: userId || null,
      decidedAt: serverTimestamp(),
      decisionNote: note || "",
      updatedAt: serverTimestamp()
    });
    await StatusHistoryService.append({
      entityType: "approvalWorkflows",
      entityId: id,
      previousStatus: "pending",
      newStatus: decision,
      userId,
      note: note || undefined
    });

    if (item.requestedBy) {
      await NotificationEngineService.notifyUser(
        item.requestedBy,
        "approval",
        `Approval ${item.code} ${decision}`
      );
    }
    await AuditService.logChange({
      userId,
      action: "APPROVAL_DECIDE",
      entityType: "approvalWorkflows",
      entityId: id,
      previousValue: { status: "pending" },
      newValue: { status: decision }
    });
    return this.get(id);
  }
};

/** Notification helpers on top of existing NotificationService. */
export const NotificationEngineService = {
  TEMPLATES: {
    approval: "Approval update",
    stock: "Stock alert",
    order: "Order update",
    system: "System"
  },

  async notifyUser(userId, type, message, meta = {}) {
    if (!userId) return null;
    // Rules only allow notifying accounts in the sender's hierarchy; a denied
    // notification must not fail the action that triggered it.
    try {
      await NotificationService.create(userId, type || "system", message);
    } catch (err) {
      console.warn("[NotificationEngine] notification not delivered:", err?.code || err);
      return false;
    }
    // Optional structured log
    try {
      await addDoc(collection(db, "notificationEvents"), {
        userId,
        type: type || "system",
        message,
        meta,
        createdAt: serverTimestamp()
      });
    } catch {
      /* non-fatal */
    }
    return true;
  },

  async notifyMany(userIds, type, message) {
    const ids = [...new Set((userIds || []).filter(Boolean))];
    await Promise.all(ids.map((uid) => this.notifyUser(uid, type, message)));
    return ids.length;
  },

  async broadcast(type, message, userIds) {
    return this.notifyMany(userIds, type, message);
  },

  async listEvents(max = 50) {
    return listOrdered("notificationEvents", max);
  }
};

/**
 * Finance integration: create accounting invoices from SCM documents.
 * Uses existing InvoiceService; stores link on financeLinks collection.
 */
export const FinanceLinkService = {
  async list() {
    return listOrdered("financeLinks");
  },

  async createFromSalesOrder(salesOrder, userId) {
    if (!salesOrder?.id) throw new Error("SO_REQUIRED");
    const invoiceId = await InvoiceService.createInvoice({
      type: "sales",
      status: "draft",
      customerName: salesOrder.customerName || "",
      currency: salesOrder.currency || "USD",
      totalAmount: salesOrder.totalAmount || 0,
      lines: (salesOrder.lines || []).map((l) => ({
        description: l.name || l.sku,
        quantity: l.quantity,
        unitPrice: l.unitPrice,
        amount: (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0)
      })),
      sourceType: "salesOrders",
      sourceId: salesOrder.id,
      sourceCode: salesOrder.code,
      createdBy: userId || null
    });

    const link = {
      code: codeStamp("FIN"),
      sourceType: "salesOrders",
      sourceId: salesOrder.id,
      sourceCode: salesOrder.code,
      invoiceId,
      amount: salesOrder.totalAmount || 0,
      currency: salesOrder.currency || "USD",
      status: "linked",
      createdAt: serverTimestamp(),
      createdBy: userId || null,
      deletedAt: null
    };
    const ref = await addDoc(collection(db, "financeLinks"), link);
    await NotificationEngineService.notifyUser(
      userId,
      "order",
      `Invoice created from ${salesOrder.code}`
    );
    await AuditService.logChange({
      userId,
      action: "FINANCE_LINK_SO",
      entityType: "financeLinks",
      entityId: ref.id,
      newValue: { invoiceId, sourceCode: salesOrder.code }
    });
    return { id: ref.id, invoiceId, ...link };
  },

  async createFromPurchaseOrder(purchaseOrder, userId) {
    if (!purchaseOrder?.id) throw new Error("PO_REQUIRED");
    const invoiceId = await InvoiceService.createInvoice({
      type: "purchase",
      status: "draft",
      vendorName: purchaseOrder.supplierName || "",
      currency: purchaseOrder.currency || "USD",
      totalAmount: purchaseOrder.totalAmount || 0,
      lines: (purchaseOrder.lines || []).map((l) => ({
        description: l.name || l.sku,
        quantity: l.quantity,
        unitPrice: l.unitPrice,
        amount: (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0)
      })),
      sourceType: "purchaseOrders",
      sourceId: purchaseOrder.id,
      sourceCode: purchaseOrder.code,
      createdBy: userId || null
    });

    const link = {
      code: codeStamp("FIN"),
      sourceType: "purchaseOrders",
      sourceId: purchaseOrder.id,
      sourceCode: purchaseOrder.code,
      invoiceId,
      amount: purchaseOrder.totalAmount || 0,
      currency: purchaseOrder.currency || "USD",
      status: "linked",
      createdAt: serverTimestamp(),
      createdBy: userId || null,
      deletedAt: null
    };
    const ref = await addDoc(collection(db, "financeLinks"), link);
    return { id: ref.id, invoiceId, ...link };
  }
};

/** SCM KPI / report aggregates for dashboard & reports screen. */
export const ScmReportingService = {
  async getKpis() {
    const [
      products,
      purchaseOrders,
      salesOrders,
      manufacturingOrders,
      exportOrders,
      salesReturns,
      pendingApprovals,
      financeLinks,
      openRmas,
      criticalish
    ] = await Promise.all([
      countCollection("products"),
      countCollection("purchaseOrders"),
      countCollection("salesOrders"),
      countCollection("manufacturingOrders"),
      countCollection("exportOrders"),
      countCollection("salesReturns"),
      ApprovalEngineService.listPending().then((r) => r.length),
      countCollection("financeLinks"),
      listOrdered("rmas", 100).then((r) => r.filter((x) => !["closed", "rejected"].includes(x.status)).length),
      listOrdered("products", 500).then((prods) =>
        prods.filter((p) => {
          const stock = Number(p.currentStock) || 0;
          const min = Number(p.minStock ?? p.reorderPoint ?? 0);
          return min > 0 && stock <= min;
        }).length
      )
    ]);

    return {
      products,
      purchaseOrders,
      salesOrders,
      manufacturingOrders,
      exportOrders,
      salesReturns,
      pendingApprovals,
      financeLinks,
      openRmas,
      lowStock: criticalish,
      generatedAt: new Date().toISOString()
    };
  },

  async getPipelineSummary() {
    const [sos, mos, pos, exports_] = await Promise.all([
      listOrdered("salesOrders", 100),
      listOrdered("manufacturingOrders", 100),
      listOrdered("purchaseOrders", 100),
      listOrdered("exportOrders", 100)
    ]);

    const byStatus = (rows) =>
      rows.reduce((acc, r) => {
        const s = r.status || "unknown";
        acc[s] = (acc[s] || 0) + 1;
        return acc;
      }, {});

    return {
      salesOrders: byStatus(sos),
      manufacturingOrders: byStatus(mos),
      purchaseOrders: byStatus(pos),
      exportOrders: byStatus(exports_)
    };
  },

  async getRecentActivity(max = 20) {
    try {
      return await listOwned("auditLogs", { max, includeDeleted: true });
    } catch {
      return [];
    }
  }
};
